import React from 'react'
import { Spin, Tag, Typography } from 'antd'
import { sellerApi } from 'api/seller'
import { IStockConfirmationStatus } from 'models/seller'
import { useStoreHiddenSync } from 'shared/hooks/useStoreHiddenSync'
import { showServerError } from 'shared/utils/showServerError'
import { StockTermsHint } from './ui/StockTermsHint'
import {
  formatStockConfirmationDate,
  getStockConfirmationSourceText,
  getStockConfirmationTag,
  getStockDaysText
} from './utils/stockConfirmation'
import styles from './StockConfirmationStatus.module.scss'

interface IStockConfirmationStatusProps {
  sellerId?: string
  className?: string
  // Статус, уже загруженный родителем: избавляет от второго запроса на странице
  status?: IStockConfirmationStatus | null
  isLoading?: boolean
}

export const StockConfirmationStatus: React.FC<IStockConfirmationStatusProps> = ({
  sellerId,
  className,
  status: controlledStatus,
  isLoading: controlledIsLoading
}) => {
  const isControlled = controlledStatus !== undefined
  const syncStoreHidden = useStoreHiddenSync()

  const [ownStatus, setOwnStatus] = React.useState<IStockConfirmationStatus | null>(null)
  const [isOwnLoading, setIsOwnLoading] = React.useState(false)

  React.useEffect(() => {
    if (isControlled) {
      return
    }

    const loadStatus = async () => {
      try {
        setIsOwnLoading(true)
        const response = await sellerApi.getStockConfirmationStatus(sellerId)
        setOwnStatus(response.data)
        syncStoreHidden(response.data)
      } catch (e) {
        showServerError(e)
      } finally {
        setIsOwnLoading(false)
      }
    }

    loadStatus()
  }, [sellerId, isControlled, syncStoreHidden])

  const status = isControlled ? controlledStatus ?? null : ownStatus
  const isLoading = isControlled ? Boolean(controlledIsLoading) : isOwnLoading

  const tag = getStockConfirmationTag(status)

  return (
    <section className={`${styles.root} ${className ?? ''}`}>
      <div className={styles.content}>
        <div className={styles.titleRow}>
          <Typography.Text className={styles.title}>
            Актуальность остатков
          </Typography.Text>
          {isLoading ? <Spin size='small' /> : <Tag color={tag.color}>{tag.label}</Tag>}
        </div>
        <div className={styles.meta}>
          <Typography.Text className={styles.metaItem}>
            Последнее обновление:{' '}
            <span className={styles.metaValue}>
              {status && !status.lastConfirmedAt
                ? 'ни разу'
                : formatStockConfirmationDate(status?.lastConfirmedAt)}
            </span>
          </Typography.Text>
          <Typography.Text className={styles.metaItem}>
            Способ:{' '}
            <span className={styles.metaValue}>
              {getStockConfirmationSourceText(status)}
            </span>
          </Typography.Text>
          <Typography.Text className={styles.metaItem}>
            Без обновления:{' '}
            <span className={styles.metaValue}>{getStockDaysText(status)}</span>
            <StockTermsHint />
          </Typography.Text>
        </div>
      </div>
    </section>
  )
}
